import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import UIModal from "@src/components/UIModal/UIModal";
import { UIButton } from "@src/components/UIButton";
import { theme } from "@src/theme";
import UploadIcon from "@src/components/FileDropzone/assets/UploadIcon";
import heicConvert from "heic-convert";
import { useNotification } from "@src/context/NotificationContext";
import Spinner from "@src/components/Spinner";
const ModalContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }): string => theme.margins.medium};
  width: 100%;
  min-width: 500px;
`;

const ImageUploadArea = styled.div<{
  $isDragging: boolean;
  $hasImage: boolean;
  $imageUrl?: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  width: 100%;
  border: 2px solid ${({ theme }): string => theme.colors.darkGray};
  border-radius: 8px;
  background-color: ${({ theme, $isDragging }): string =>
    $isDragging ? theme.colors.darkGray : theme.colors.gray150};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  position: relative;
  overflow: hidden;

  &:hover {
    background-color: ${({ theme }): string => theme.colors.yellow};
  }

  ${({ $hasImage, $imageUrl }): string =>
    $hasImage && $imageUrl
      ? `
    background-image: url(${$imageUrl});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  `
      : ""}
`;

const UploadIconContainer = styled.div`
  color: ${({ theme }): string => theme.colors.black};
  margin-bottom: ${({ theme }): string => theme.margins.small};
  z-index: 1;
`;

const UploadText = styled.span`
  color: ${({ theme }): string => theme.colors.darkGray};
  font-weight: bold;
  text-align: center;
  font-size: 14px;
  z-index: 1;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  z-index: 2;

  &:hover {
    opacity: 1;
  }
`;

const CaptionInputContainer = styled.div`
  width: 100%;
`;

const CaptionInput = styled.input`
  width: 100%;
  height: 50px;
  background: transparent;
  border: 2px solid ${({ theme }): string => theme.colors.darkGray};
  border-radius: 8px;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 14px;
  outline: none;
  text-align: center;

  &::placeholder {
    color: ${({ theme }): string => theme.colors.darkGray};
  }

  &:focus {
    border-color: ${({ theme }): string => theme.colors.yellow};
  }
`;

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (file: File | null, caption?: string) => void;
  initialImage?: string | null;
  initialCaption?: string;
  /** Hide the caption input for images that have no caption. */
  hideCaption?: boolean;
  /** When set, shows a button that calls it and closes the modal. */
  onRemove?: () => void;
  removeLabel?: string;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialImage,
  initialCaption,
  hideCaption = false,
  onRemove,
  removeLabel = "Remove image",
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(
    initialImage || null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [caption, setCaption] = useState(initialCaption || "");

  useEffect(() => {
    if (!isOpen) return;
    setSelectedFile(null);
    setImagePreview(initialImage || null);
    setCaption(initialCaption || "");
  }, [isOpen, initialImage, initialCaption]);

  const hasInitialImage = Boolean(initialImage);
  const captionChanged = useMemo(() => {
    return (caption || "") !== (initialCaption || "");
  }, [caption, initialCaption]);

  const canSave = Boolean(selectedFile) || (hasInitialImage && captionChanged);

  const { addNotification } = useNotification();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileSelect = async (file: File): Promise<void> => {
    let processedFile = file;
    setIsProcessing(true);

    if (
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      file.name.toLowerCase().endsWith(".heic") ||
      file.name.toLowerCase().endsWith(".heif")
    ) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const outputBuffer = await heicConvert({
          buffer: new Uint8Array(arrayBuffer),
          format: "JPEG",
          quality: 0.8,
        });

        processedFile = new File(
          [outputBuffer as Uint8Array],
          file.name.replace(/\.[^/.]+$/, ".jpg"),
          { type: "image/jpeg" }
        );
      } catch {
        addNotification(
          "We couldn't process the uploaded HEIC image.",
          "error"
        );
        setIsProcessing(false);
        return;
      }
    }

    setSelectedFile(processedFile);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
      setIsProcessing(false);
    };
    reader.onerror = () => {
      addNotification("Error reading the image file.", "error");
      setIsProcessing(false);
    };
    reader.readAsDataURL(processedFile);
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleSaveClick = (): void => {
    if (!canSave) return;
    // Save new image + caption, or caption only (no new file)
    onSave(selectedFile, caption);
    setSelectedFile(null);
    setImagePreview(null);
    setCaption("");
    onClose();
  };

  const handleCancel = (): void => {
    setSelectedFile(null);
    setImagePreview(initialImage || null);
    setCaption(initialCaption || "");
    onClose();
  };

  return (
    <UIModal
      isOpen={isOpen}
      onClose={handleCancel}
      title=""
      modalStyles={{
        backgroundColor: theme.colors.gray150,
        padding: theme.margins.large,
        minWidth: "500px",
      }}
      actions={
        <>
          {onRemove && (
            <UIButton
              onClick={() => {
                onRemove();
                onClose();
              }}
              variant="outline"
              style={{
                backgroundColor: theme.colors.gray150,
                color: theme.colors.red,
                borderColor: theme.colors.red,
                marginRight: "auto",
              }}
            >
              {removeLabel}
            </UIButton>
          )}
          <UIButton
            onClick={handleCancel}
            variant="outline"
            style={{
              backgroundColor: theme.colors.gray150,
              color: theme.colors.gray,
              borderColor: theme.colors.gray,
            }}
          >
            Cancel
          </UIButton>
          <UIButton
            onClick={handleSaveClick}
            variant="primary"
            disabled={!canSave}
            style={{
              backgroundColor: theme.colors.yellow,
              color: theme.colors.black,
              minWidth: "90px",
            }}
          >
            Save
          </UIButton>
        </>
      }
    >
      <ModalContentContainer>
        <ImageUploadArea
          $isDragging={isDragging}
          $hasImage={!!imagePreview}
          $imageUrl={imagePreview || undefined}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => {
            if (!isProcessing) {
              document.getElementById("image-upload-input")?.click();
            }
          }}
        >
          {isProcessing ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: theme.margins.small,
              }}
            >
              <Spinner />
              <UploadText>Processing image...</UploadText>
            </div>
          ) : imagePreview ? (
            <ImageOverlay>
              <UploadText>Click to change image</UploadText>
            </ImageOverlay>
          ) : (
            <>
              <UploadIconContainer>
                <UploadIcon color={theme.colors.darkGray} size={48} />
              </UploadIconContainer>
              <UploadText>Click to add or drop anywhere</UploadText>
            </>
          )}
          <HiddenInput
            id="image-upload-input"
            type="file"
            accept="image/*,.heic,.heif"
            onChange={handleInputChange}
            disabled={isProcessing}
          />
        </ImageUploadArea>
        {!hideCaption && (
          <CaptionInputContainer>
            <CaptionInput
              type="text"
              placeholder="Write the image caption here if necessary."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
          </CaptionInputContainer>
        )}
      </ModalContentContainer>
    </UIModal>
  );
};

export default ImageUploadModal;
